import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { INSIGHTS_DATA, INSIGHTS_DATA_DESKTOP } from "./data/items";
import styles from "./insights.module.scss";
import Image from "@Components/Image";
import config from "@Config/index";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import TrustPilotDynamicWidget from "@Components/TrustPilotDynamicWidget";
import React from "react";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";

type props = {
  isDesktop?: boolean;
  fromBaidu?: boolean;
  className?: string;
  /**
   * Wrap the row in a full-bleed grey band. Off by default so the search-region pages that render
   * this keep the plain background they have; the home page turns it on for its banding.
   */
  greyBand?: boolean;
};

const Insights = ({ isDesktop = false, fromBaidu, className, greyBand = false }: props) => {
  const { t } = useTranslationHook();
  const { isAbove } = useResponsiveness();

  const isAboveMD = isAbove(SCREEN_SIZE.MD, false, true);
  const showDesktopInsights = isDesktop && isAboveMD;
  const DATA = showDesktopInsights ? INSIGHTS_DATA_DESKTOP : INSIGHTS_DATA;

  // Live TrustScore; the desktop tile renders Trustpilot's own widget instead.
  const trustPilotScore = useSelector(({ appData }: AppState) => appData.trustPilotScore);

  const row = (
    <div
      className={formatClassNames(
        showDesktopInsights ? styles.insightsContainerDesktop : styles.insightsContainer,
        className,
        styles.largeScreenResponsive,
      )}
    >
      {DATA.map((data, idx) => (
        <React.Fragment key={data.title}>
          {data.icon == "trustpilot" ? (
            !fromBaidu && (
              <div
                className={formatClassNames(
                  showDesktopInsights ? styles.insightDesktop : styles.insight,
                  idx === 3 ? styles.withGreenGradient : styles.withPinkGradient,
                  showDesktopInsights && styles.trustpilot,
                )}
                key={data.title}
              >
                {showDesktopInsights ? (
                  <TrustPilotDynamicWidget />
                ) : (
                  <>
                    <Image
                      src={`${
                        showDesktopInsights
                          ? `${config.IMAGE_STATIC_URL}/images/trustpilot-rating.svg`
                          : `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/${data.icon}.svg`
                      }`}
                      height={showDesktopInsights ? 46 : 32}
                      width={showDesktopInsights ? 160 : 32}
                      className={styles.icon}
                      isNotLazy
                      alt=""
                    />
                    <div
                      className={
                        showDesktopInsights
                          ? styles.detailsContainerDesktop
                          : styles.detailsContainer
                      }
                    >
                      <span className={showDesktopInsights ? styles.titleDesktop : styles.title}>
                        {t(data.title)}
                      </span>
                      <span
                        className={showDesktopInsights ? styles.subtitleDesktop : styles.subtitle}
                      >
                        {t(data.subtitle)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )
          ) : (
            <div
              className={formatClassNames(
                showDesktopInsights ? styles.insightDesktop : styles.insight,
                styles.withPinkGradient,
              )}
              key={data.title}
              data-testid={`homepage-insights-${data.title}`}
            >
              <Image
                src={
                  config.IS_CHINA_APP
                    ? `${config.IMAGE_STATIC_URL}/images/${data.icon}.svg`
                    : `${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/${data.icon}.svg`
                }
                height={showDesktopInsights ? 46 : 32}
                width={showDesktopInsights ? 46 : 32}
                className={styles.icon}
                isNotLazy
                alt=""
              />
              <div
                className={
                  showDesktopInsights ? styles.detailsContainerDesktop : styles.detailsContainer
                }
              >
                <span className={showDesktopInsights ? styles.titleDesktop : styles.title}>
                  {data.isTrustRating ? `${trustPilotScore}/5` : t(data.title)}
                </span>
                <span className={showDesktopInsights ? styles.subtitleDesktop : styles.subtitle}>
                  {t(data.subtitle)}
                </span>
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <>
      {greyBand && showDesktopInsights ? <div className={styles.greyBand}>{row}</div> : row}
      {!showDesktopInsights && <div className={styles.bottomBorder} />}
    </>
  );
};

export default wrapperHOC(Insights, {
  componentName: "Insights-Home",
  showForChina: true,
  showForMiniProgram: false,
  showForWhiteLable: false,
});
